import { action } from 'mobx';
import { getDefaultState, SearchMvpPageModel, ValidatedResultsType } from './model';
import { APIController } from '../../api/controller';
import { UserModel } from '../../user/model';
import { EnvModel } from '../../env/model';
import { ContentModel } from '../../content/model';
import { RouterModel } from '../../router/model';
import { AnalyticsController } from '../../analytics/controller';
import { UIController } from '../../ui/controller';
import { RouterState } from 'react-router';
import { FILTER_KEYS } from '../../../lib/findFilters';
import objectDifference from '../../../lib/objectDifference';
import { debounce } from '../../../components/project-happy/utilities/functions';
import { SEARCH_ROUTE } from '../../../constants';
import { SearchSuggestionType } from '../../../components/project-happy/atoms/SearchSuggestionItem';
import { GraphQLModel } from '../../graphql/model';
import {
  FindTracksDocument,
  FindTracksQueryVariables,
  GetSearchSuggestionsDocument,
  FindSortOrder,
  FindSortMode,
  GetAllMoodsAndGenresDocument,
  GetSearchSuggestionsQueryVariables,
  CatalogFilter,
  GetValidatedArtistDocument,
  GetValidatedTrackDocument,
  GetPlaylistDocument,
} from '../../../graphql/generated-types/graphql';
import { parseCatalogueType } from '../../../components/project-happy/utilities/search';
import { isEmpty } from '../../../components/project-happy/utilities/objects';
import { castKeyPair, SearchField } from '../../../components/project-happy/utilities/search';

export class SearchMvpPageController {
  constructor(
    private model: SearchMvpPageModel,
    private ui: UIController,
    private env: EnvModel,
    private router: RouterModel,
    private analytics: AnalyticsController,
    private api: APIController,
    private user: UserModel,
    private content: ContentModel,
    private graphql: GraphQLModel
  ) {
    this.syncStateWithRouter = debounce(this.syncStateWithRouter, 500);
  }
  @action
  onEnter = async (nextState: RouterState) => {
    const queryString = nextState.location.search;
    this.model.userRegion = this.user.user ? this.user.user.country : null;
    return this.init(queryString);
  };

  @action
  onLeave = async () => {
    return this.ui.scrollToTop();
  };

  @action
  searchTracks = async (searchVariables: FindTracksQueryVariables) => {
    const { data: results } = await this.graphql.client.query({
      query: FindTracksDocument,
      variables: searchVariables,
    });

    return results;
  };

  @action
  getSearchSuggestions = async (searchVariables: GetSearchSuggestionsQueryVariables) => {
    const { data: results } = await this.graphql.client.query({
      query: GetSearchSuggestionsDocument,
      variables: searchVariables,
    });

    return results;
  };

  @action
  getValidatedArtistResults = async (identity: string) => {
    const { data: results } = await this.graphql.client.query({
      query: GetValidatedArtistDocument,
      variables: {
        options: {
          identity,
        },
        filters: {
          artist: identity,
        },
      },
    });

    return results;
  };

  @action
  getValidatedTrackResults = async (identity: string) => {
    const { data: results } = await this.graphql.client.query({
      query: GetValidatedTrackDocument,
      variables: {
        options: {
          identity,
        },
        filters: {
          track: identity,
        },
      },
    });

    return results;
  };

  @action
  getValidatedPlaylistResults = async (identity: string) => {
    const { data: results } = await this.graphql.client.query({
      query: GetPlaylistDocument,
      variables: {
        options: {
          identity,
        },
      },
    });

    return results;
  };

  /**
   * Fetches mood and genre data displayed in the
   * search filter sidebar
   */
  @action
  fetchMoodAndGenreItems = async () => {
    const { error, data: entities } = await this.graphql.client.query({
      query: GetAllMoodsAndGenresDocument,
    });

    if (!error) {
      this.model.moods = entities.moods.matches.results;
      this.model.genres = entities.genres.matches.results;
    }
  };

  @action
  performValidatedSearch = async (type: string, identity: string) => {
    const { validatedSearchParams } = this.model;
    let validatedSearchResults: ValidatedResultsType = null;

    if (type === 'artist') {
      validatedSearchResults = await this.getValidatedArtistResults(identity);
    }
    if (type === 'track') {
      validatedSearchResults = await this.getValidatedTrackResults(identity);
    }
    if (type === 'playlist') {
      validatedSearchResults = await this.getValidatedPlaylistResults(identity);
    }

    if (!validatedSearchResults) return;

    this.model.validatedSearchResults = validatedSearchResults;

    const suggestion = this.model.formattedValidatedSearchResults.find(
      (entity) => entity.identity === validatedSearchParams.identity && entity.type === validatedSearchParams.type
    );

    if (suggestion) {
      this.model.selectedSuggestion = suggestion;
    }

    if (suggestion?.type === 'track' && 'track' in this.model.validatedSearchResults) {
      this.model.results = [this.model.validatedSearchResults.track];
      this.model.pagination = { ...this.model.pagination, total: 1, totalPages: 1 };
    }
  };

  @action
  performSearch = async (clearFilter = false, resetLoader = true) => {
    // Reset any errors
    this.model.tracksApiError = false;
    this.model.noSearchResult = false;

    // resetLoader will always be True except on loadMore clicks
    this.model.loading = resetLoader;

    const { searchVariables, defaultSearchState, validatedSearchParams } = this.model;

    if (clearFilter) {
      this.model.searchState.filters = defaultSearchState.filters;
      this.clearValidatedSearch();
    }

    if (resetLoader) {
      this.model.results = [];
    }

    if (this.model.showSuggestionDropdown) {
      this.model.showSuggestionDropdown = false;
    }

    this.model.searchVariables.filters.matchArtistFallback = true;
    this.model.searchVariables.filters.availableIn = this.model.userRegion;

    try {
      if (validatedSearchParams) {
        await this.performValidatedSearch(validatedSearchParams.type, validatedSearchParams.identity);
      }

      if (!this.model.isValidatedSearch || !isEmpty(this.model.validatedSearchTrackFilters)) {
        const {
          find: {
            tracks: { results, pagination },
          },
        } = await this.searchTracks({ ...searchVariables, ...this.model.validatedSearchTrackFilters });

        this.model.pagination = { ...this.model.pagination, ...pagination };

        if (!resetLoader) {
          this.model.results = this.model.results.concat(results);
        } else {
          this.model.results = results;
        }

        if (this.model.results.length === 0) {
          this.model.noSearchResult = true;
        }
      }

      this.resetCursor();
    } catch (error) {
      this.model.tracksApiError = true;
      throw error;
    } finally {
      this.model.loading = false;
    }
  };

  @action
  resetQuery = () => {
    this.model.noSearchResult = false;
    this.model.searchState.query = '';
    this.clearValidatedSearch();
    this.syncStateWithRouter();
  };

  @action
  clearValidatedSearch = () => {
    this.model.selectedSuggestion = null;
    this.model.validatedSearchResults = null;
    this.model.validatedSearchParams = null;
  };

  @action
  clearAllFilters = () => {
    this.resetFilters();
    this.model.sortModeManuallyChanged = false;
    this.clearValidatedSearch();
    this.syncStateWithRouter();
    this.hideFilters();
  };

  @action resetCursor = () => {
    this.model.cursor = null;
  };

  @action
  refreshPage = () => {
    const { defaultSearchState } = this.model;
    this.model.searchState = defaultSearchState;
    this.clearValidatedSearch();
    this.analytics.sendMixpanel('User clicks start over');
    this.performSearch();
  };

  @action
  performAutoSuggestion = async (val: string) => {
    this.model.suggestionsApiError = false;

    if (val.length < 3) {
      this.model.showSuggestionDropdown = false;
    }

    if (val.length >= 3) {
      // Reset suggestions and show the loading state
      this.model.suggestions = {};
      this.model.loadingSuggestions = true;
      this.model.showSuggestionDropdown = true;

      this.model.searchVariables.filters.matchArtistFallback = undefined;
      this.model.searchVariables.filters.availableIn = this.model.userRegion;

      try {
        await this.getUserSearchHistory();
        const suggestions = await this.getSearchSuggestions({
          query: val,
          sortMode: FindSortMode.PopularSpotify,
          sortOrder: FindSortOrder.Desc,
        });

        if (suggestions) {
          this.model.suggestions = suggestions;
        }
      } catch (error) {
        this.model.suggestionsApiError = true;
        console.error(error);
      }

      // Temporary implementation of 'instrumental' search suggestion [WEB-8279]
      const instrumentalRegex = /\b(inst)(?:(r|ru|rum|rume|rumen|rument|rumenta|rumental)?)\b/;

      if (instrumentalRegex.test(val.toLowerCase())) {
        this.model.showInstrumentalSuggestion = true;
      } else {
        this.model.showInstrumentalSuggestion = false;
      }

      // Now that everything is done we can hide the loading state
      this.model.loadingSuggestions = false;

      if (
        this.model.getFormattedSuggestions.length < 1 &&
        !this.model.showInstrumentalSuggestion &&
        !this.model.searchHistory.length
      ) {
        this.model.showSuggestionDropdown = false;
      } else if (!this.model.showSuggestionDropdown) {
        this.model.showSuggestionDropdown = true;
      }
    }

    this.resetCursor();
  };

  @action
  loadMore = async () => {
    this.model.loadingMoreResults = true;

    this.model.searchState.pagination.from = this.model.searchState.pagination.from + 25;
    await this.performSearch(false, false);

    this.model.loadingMoreResults = false;
  };

  @action
  getUserSearchHistory = async () => {
    if (!this.user.user) return;

    try {
      const history = await this.api.search.fetchUserSearchHistory(this.user.user.identity);
      this.model.searchHistory =
        history &&
        history.data.slice(0, 3).map((historyItem) => {
          return {
            ...historyItem,
            type: 'history',
            name: historyItem.term,
          };
        });
    } catch (e) {
      return e;
    }
  };

  @action
  saveUserSearchTerm = async (entity: SearchSuggestionType) => {
    if (!entity?.slug || !entity.name || entity.type === 'history') return;

    if (entity.type === 'playlist') {
      entity = { ...this.model.playlistDetails, ...entity };
    }

    try {
      await this.api.search.addUserSearchHistory(this.user.user.identity, entity);
    } catch (e) {
      return e;
    }
  };

  @action
  deleteUserSearchTerm = async (historyId: string) => {
    try {
      await this.api.search.deleteUserSearchHistory(this.user.user.identity, historyId);
      await this.getUserSearchHistory();
    } catch (e) {
      return e;
    }
  };
  @action
  resetPagination = () => (this.model.searchState.pagination = this.model.defaultSearchState.pagination);

  @action
  resetFilters = () => {
    this.model.searchState.filters = this.model.defaultSearchState.filters;
  };

  @action
  handleSuggestionSelection = async (suggestion: SearchSuggestionType) => {
    const { type, slug, name, identity } = suggestion;

    if (type === 'mood' || type === 'genre' || type === 'instrumental') {
      // clear all initial filters before filtering by suggestion selection
      this.model.searchState.filters = this.model.defaultSearchState.filters;
      this.model.searchVariables.query = '';
      this.refineFilter(type, type === 'instrumental' ? true : slug);
    } else {
      this.model.searchVariables.query = name;

      if (identity) {
        this.model.validatedSearchParams = {
          identity,
          type,
        };
      }

      this.saveUserSearchTerm(suggestion);
      this.resetFilters();
    }

    this.model.showSuggestionDropdown = false;
    this.resetPagination();
    this.hideFilters();
    this.syncStateWithRouter();
  };

  @action
  toggleFilter = async () => {
    this.model.openFilter = !this.model.openFilter;
  };

  @action hideFilters = () => {
    this.model.openFilter = false;
  };

  private queryStringToFilterState = (qs: string) => {
    const defaultState = getDefaultState();

    const paramsAsKeyValuePairs = qs.split('&').reduce((pairs, pair) => [...pairs, pair.split('=')], []);

    return paramsAsKeyValuePairs.reduce((state, [key, value]: [SearchField, any]) => {
      const decoded = castKeyPair(key, decodeURIComponent(value));

      switch (true) {
        case ['size', 'from'].includes(key):
          return {
            ...state,
            pagination: {
              ...state.pagination,
              [key]: decoded,
            },
          };

        case FILTER_KEYS.includes(key as any):
          return {
            ...state,
            filters: {
              ...state.filters,
              [key]: decoded,
            },
          };

        default:
          return {
            ...state,
            [key]: decoded,
          };
      }
    }, defaultState);
  };

  @action
  refineSort = (mode: FindSortMode, order: FindSortOrder | undefined = undefined) => {
    this.model.searchState.sortMode = mode;
    this.model.searchState.sortOrder = order;
    this.model.sortModeManuallyChanged = true;
    this.syncStateWithRouter();
  };

  /**
   * Update the search state from the URL query string
   */
  @action
  parseStateFromRouter = (queryString: string) => {
    if (!queryString.length) {
      this.model.searchState = getDefaultState();
      this.clearValidatedSearch();
    } else {
      const params = queryString.split('?')[1];

      const paramsAsUrlParams = new URLSearchParams(params);
      const isValidated = paramsAsUrlParams.has('validatedSearchId') && paramsAsUrlParams.has('validatedSearchId');

      if (isValidated) {
        const validatedSearchId = paramsAsUrlParams.get('validatedSearchId');
        const validatedSearchType = paramsAsUrlParams.get('validatedSearchType');

        this.model.validatedSearchParams = {
          identity: validatedSearchId,
          type: validatedSearchType,
        };
      } else {
        this.clearValidatedSearch();
      }

      this.model.searchState = params.length ? this.queryStringToFilterState(params) : getDefaultState();
    }
  };

  handleFilterCountBounce = () => {
    this.model.filterCountBounce = true;
    // Reset the bouncing state after the animation finishes (1 second in this case)
    setTimeout(() => {
      this.model.filterCountBounce = false;
    }, 1000);
  };

  @action
  updateMoodOrGenre = (key: 'mood' | 'genre', slug: string, toggle = true) => {
    const initialValue = this.model.searchVariables.filters[key];

    let newValue = initialValue || '';

    if (!newValue.length) {
      newValue = slug;
    } else {
      const existingTags = newValue.split(',') || [];

      if (existingTags.includes(slug)) {
        if (toggle) {
          newValue = existingTags.filter((s: string) => s !== slug).join(',');
        }
      } else {
        newValue += `,${slug}`;
      }
    }

    this.refineFilter(key, newValue || undefined);
  };

  @action
  refineFilter = async (key: string, value: any) => {
    this.resetPagination();
    this.clearValidatedSearch();

    const [MIN_DURATION, MAX_DURATION] = this.model.durationRange;
    const [MIN_BPM, MAX_BPM] = this.model.bpmRange;
    const initialCount = this.model.filterCount;

    switch (key) {
      case 'durationMin':
        this.model.searchState.filters.durationMin =
          value <= MIN_DURATION || value === null
            ? null
            : Math.min(
                Math.max(value as number, MIN_DURATION),
                this.model.searchVariables.filters.durationMax || MAX_DURATION
              );
        break;

      case 'durationMax':
        this.model.searchState.filters.durationMax =
          value >= MAX_DURATION || value === null
            ? null
            : Math.max(
                Math.min(value as number, MAX_DURATION),
                this.model.searchVariables.filters.durationMin || MIN_DURATION
              );
        break;
      case 'tempoRange':
        this.model.searchState.filters['tempoMin'] = value[0] <= MIN_BPM ? null : value[0];
        this.model.searchState.filters['tempoMax'] = value[1] >= MAX_BPM ? null : value[1];
        break;
      case 'tempoMin':
        this.model.searchState.filters.tempoMin =
          value <= MIN_BPM || value === null
            ? null
            : Math.min(Math.max(value as number, MIN_BPM), this.model.searchVariables.filters.tempoMax || MAX_BPM);
        break;

      case 'tempoMax':
        this.model.searchState.filters.tempoMax =
          value >= MAX_BPM || value === null
            ? null
            : Math.max(Math.min(value as number, MAX_BPM), this.model.searchVariables.filters.tempoMin || MIN_BPM);
        break;

      case 'catalogueType':
        this.model.searchState.filters[key] = value;

        // Update the sortMode if the user hasn't manually changed it
        if (!this.model.sortModeManuallyChanged) {
          const { sortMode, sortOrder } = this.getDefaultSortForCatalogueType(value);
          this.model.searchState.sortMode = sortMode;
          this.model.searchState.sortOrder = sortOrder;
        }
        break;

      default:
        this.model.searchState.filters[key] = value;
        this.model.searchState = { ...this.model.searchState };
    }

    this.syncStateWithRouter();
    if (this.model.filterCount !== initialCount) {
      this.handleFilterCountBounce();
    }
  };

  /**
   * Update the Url with the search or filter query.
   *
   * This will trigger `performSearch` via the reaction in `mvp/resources/assets/components/project-happy/pages/Search.tsx`
   */
  @action
  syncStateWithRouter = (routerPush = true) => {
    if (this.router.location.pathname !== SEARCH_ROUTE) return;

    // Reset pagination to default, so it won't show either size or from in the Url query
    const allQueryExceptPagination = {
      ...this.model.searchState,
      pagination: this.model.defaultSearchState.pagination,
    };
    const qs = this.filterStateToQueryString(allQueryExceptPagination);

    const params = new URLSearchParams(qs);

    if (this.model.validatedSearchParams) {
      params.set('validatedSearchId', encodeURIComponent(this.model.validatedSearchParams.identity));
      params.set('validatedSearchType', encodeURIComponent(this.model.validatedSearchParams.type));
    } else {
      params.delete('validatedSearchId');
      params.delete('validatedSearchType');
    }

    const paramsAsString = params.toString().replace(/\+/g, '%20');

    const nextPath = SEARCH_ROUTE + (paramsAsString.length ? `?${paramsAsString}` : '');

    if (routerPush) {
      this.router.push(nextPath);
    } else {
      this.router.replace(nextPath);
    }

    const { query } = this.model.searchState;

    this.ui.setSEO(SEARCH_ROUTE, { query });
  };

  private filterStateToQueryString = (state: FindTracksQueryVariables) => {
    const diff = objectDifference(state, this.model.defaultSearchState);
    const queryPaths = [];
    const excludedValues = ['availableIn', 'matchArtistFallback'];

    const encode = (key, value) => `${key}=${encodeURIComponent(value as string)}`;

    Object.keys(diff).forEach((key) => {
      if (key === 'pagination')
        return Object.keys(diff.pagination).forEach((k) => {
          queryPaths.push(encode(k, diff.pagination[k]));
        });

      if (key === 'filters')
        return Object.keys(diff.filters).forEach((k) => {
          // We don't want to add this key to the query string
          if (excludedValues.includes(k)) {
            return;
          }
          queryPaths.push(encode(k, diff.filters[k]));
        });

      queryPaths.push(encode(key, diff[key]));
    });

    return queryPaths.join('&');
  };

  /** Change sortMode when catalogueType changes */
  @action
  getDefaultSortForCatalogueType = (
    catalogueType: CatalogFilter | null
  ): { sortMode: FindSortMode; sortOrder: FindSortOrder } => {
    // Stock - Included
    if (catalogueType === 'PRODUCTION') {
      return {
        sortMode: FindSortMode.PopularLickd,
        sortOrder: FindSortOrder.Desc,
      };
    }
    // Chart - Premium
    else if (catalogueType === 'CHART') {
      return {
        sortMode: FindSortMode.PopularSpotify,
        sortOrder: FindSortOrder.Desc,
      };
    } else {
      return {
        sortMode: FindSortMode.PopularSpotify,
        sortOrder: FindSortOrder.Desc,
      };
    }
  };

  /** Fired on page load, this function does the following:
   *
   * - Fetches the mood and genre items for the filters sidebar
   * - Parses the URL search params and updates the search state
   * - Performs an initial search based on the parameters provided
   */
  @action
  init = async (queryString: string) => {
    await this.ui.setSEO(SEARCH_ROUTE);
    this.fetchMoodAndGenreItems();
    this.parseStateFromRouter(queryString);
    this.performSearch();

    return;
  };
}
